/**
 * 本部PLビュー(ステップ9)。
 * 共通サマリー5行(売上/原価/粗利/経費/部署利益)+ 科目別内訳を表示する。
 * 実績・見込みの振り分けは plEntryOf()(実績=承認済み以降・計上日の月)。見込みは別枠で示す。
 */
import { useCallback, useEffect, useState } from 'react';
import { useMasters } from '@core/masters/MastersProvider';
import { usePeriod } from '@core/components/PeriodProvider';
import { useToast } from '@core/components/Toast';
import { formatDiff, formatYen, achievementRate } from '@core/lib/money';
import { toMonthLabel } from '@core/lib/date';
import type { DepartmentSummary, SummaryLine } from '@core/lib/types';
import { HONBU_DEPARTMENT_ID } from '@modules/honbu';
import { getDepartmentModule, getDepartmentPendingSummary } from './moduleRegistry';
import {
  breakdownByAccount,
  fetchBudgets,
  fetchBookedTransactions,
  type AccountBreakdown,
} from './plRepo';

const ROWS: { key: keyof SummaryLine; label: string; strong?: boolean }[] = [
  { key: 'revenue', label: '売上' },
  { key: 'cogs', label: '原価' },
  { key: 'grossProfit', label: '粗利', strong: true },
  { key: 'expense', label: '経費' },
  { key: 'departmentProfit', label: '部署利益', strong: true },
];

function RateCell({ budget, actual }: { budget: number; actual: number }) {
  const rate = achievementRate(budget, actual);
  if (rate === null) {
    return (
      <div className="rate-wrap">
        <span className="num">—</span>
      </div>
    );
  }
  return (
    <div className="rate-wrap">
      <div className="rate-bar">
        <i className={rate < 95 ? 'low' : ''} style={{ width: `${Math.min(rate, 100)}%` }} />
      </div>
      <span className="num">{rate}%</span>
    </div>
  );
}

export default function DepartmentPlPage({ departmentId }: { departmentId?: string }) {
  const deptId = departmentId ?? HONBU_DEPARTMENT_ID;
  const { accounts, departmentName } = useMasters();
  const { month } = usePeriod();
  const toast = useToast();
  const [summary, setSummary] = useState<DepartmentSummary | null>(null);
  const [pending, setPending] = useState<SummaryLine | null>(null);
  const [breakdown, setBreakdown] = useState<AccountBreakdown[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (accounts.length === 0) return;
    setLoading(true);
    try {
      const module = getDepartmentModule(deptId, accounts);
      const [result, pendingSummary, confirmed, budgets] = await Promise.all([
        module.getMonthlySummary(month),
        getDepartmentPendingSummary(deptId, month, accounts),
        fetchBookedTransactions(deptId, month),
        fetchBudgets(deptId, month),
      ]);
      setSummary(result);
      setPending(pendingSummary);
      setBreakdown(breakdownByAccount(confirmed, budgets, accounts));
    } catch (e) {
      toast(e instanceof Error ? e.message : 'PLデータを取得できませんでした');
    } finally {
      setLoading(false);
    }
  }, [accounts, deptId, month, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading || !summary) {
    return (
      <section className="screen">
        <div className="card">
          <p className="empty">読み込み中…</p>
        </div>
      </section>
    );
  }

  const hasBudget = summary.budget.revenue + summary.budget.cogs + summary.budget.expense > 0;

  return (
    <section className="screen">
      <div className="card">
        <h3>
          {departmentName(deptId)}PL({toMonthLabel(month)})
          <span className="hint" style={{ fontWeight: 400, marginLeft: 8 }}>
            — 実績は承認済み以降の取引を計上日の月で集計しています
          </span>
        </h3>
        <table>
          <thead>
            <tr>
              <th style={{ width: '32%' }}>項目</th>
              <th className="num">予算</th>
              <th className="num">実績</th>
              <th className="num">差異</th>
              <th className="num" style={{ width: 130 }}>
                達成率
              </th>
            </tr>
          </thead>
          <tbody>
            {ROWS.map(({ key, label, strong }) => {
              const budget = summary.budget[key];
              const actual = summary.actual[key];
              const diff = actual - budget;
              return (
                <tr key={key}>
                  <td style={{ fontWeight: strong ? 700 : 400 }}>{label}</td>
                  <td className="num">{formatYen(budget)}</td>
                  <td className="num" style={{ fontWeight: strong ? 700 : 400 }}>
                    {formatYen(actual)}
                  </td>
                  <td className={`num ${diff >= 0 ? 'pos' : 'neg'}`}>{formatDiff(diff)}</td>
                  <td>
                    <RateCell budget={budget} actual={actual} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {!hasBudget && (
          <p className="hint" style={{ marginTop: 10 }}>
            予算は未入力です(予算入力はフェーズ2で実装します)。
          </p>
        )}
      </div>

      {pending && (pending.revenue > 0 || pending.cogs > 0 || pending.expense > 0) && (
        <div className="card">
          <h3>見込み(実績には含みません)</h3>
          {pending.revenue > 0 && (
            <p>
              売上: <b>{formatYen(pending.revenue)}</b> 円
            </p>
          )}
          {pending.cogs > 0 && (
            <p>
              原価: <b>{formatYen(pending.cogs)}</b> 円
            </p>
          )}
          {pending.expense > 0 && (
            <p>
              経費: <b>{formatYen(pending.expense)}</b> 円
            </p>
          )}
          <p className="hint" style={{ marginTop: 8 }}>
            申請中・承認待ちと、計上日の月がまだ来ていない分です。承認され、計上日の月になった時点で実績に計上されます。
          </p>
        </div>
      )}

      <div className="card">
        <h3>科目別の内訳(実績)</h3>
        {breakdown.length === 0 ? (
          <p className="empty">この月の実績データはまだありません。</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>科目</th>
                <th>PL行</th>
                <th className="num">予算</th>
                <th className="num">実績</th>
                <th className="num">差異</th>
              </tr>
            </thead>
            <tbody>
              {breakdown.map((row) => {
                const diff = row.actual - row.budget;
                return (
                  <tr key={row.accountId}>
                    <td>{row.accountName}</td>
                    <td className="hint">
                      {row.plLine === 'revenue'
                        ? '売上'
                        : row.plLine === 'cogs'
                          ? '原価'
                          : row.plLine === 'expense'
                            ? '経費'
                            : '—'}
                    </td>
                    <td className="num">{formatYen(row.budget)}</td>
                    <td className="num">{formatYen(row.actual)}</td>
                    <td className={`num ${diff >= 0 ? 'pos' : 'neg'}`}>{formatDiff(diff)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}
